(function () {
  'use strict';

  const SELECTORS = {
    form: '.klaviyo-form',
    closeButton: '.klaviyo-close-form',
    cookieBanner: '#shopify-pc__banner, .ck-Banner[data-module="cookie-banner"]',
    backdrop: '.needsclick.kl-private-reset-css-Xuajs1',
    trigger: '[data-klaviyo-trigger]',
    formElements: 'input, select, textarea, label, button'
  };

  const COOKIE_BANNER_IDS = [
    'shopify-pc__banner__btn-accept',
    'shopify-pc__banner__btn-decline'
  ];

  const COOKIE_BANNER_CLASSES = [
    'shopify-pc__banner__btn-accept',
    'shopify-pc__banner__btn-decline'
  ];

  const RESTORE_CONFIG = {
    maxAttempts: 20,
    interval: 10,
    timeout: 50,
    cookieBannerWindow: 1000
  };

  // State variables - declared at top level for clarity
  let cookieBannerClickTime = 0;
  let isRestoring = false;
  let restoreIntervalId = null;
  let mutationObserver = null;

  function getFormId() {
    try {
      if (window.__klaviyoFormId) return window.__klaviyoFormId;
      const trigger = document.querySelector(SELECTORS.trigger);
      if (trigger && trigger.dataset) return trigger.dataset.klaviyoTrigger;
      if (window._klOnsite && Array.isArray(window._klOnsite) && window._klOnsite.length > 0) {
        for (let j = window._klOnsite.length - 1; j >= 0; j--) {
          if (Array.isArray(window._klOnsite[j]) && window._klOnsite[j][0] === 'openForm') {
            return window._klOnsite[j][1];
          }
        }
      }
    } catch (error) {
      console.warn('Klaviyo popup fix: Error getting form ID', error);
    }
    return null;
  }

  function getAllKlaviyoForms() {
    try {
      return Array.from(document.querySelectorAll(SELECTORS.form));
    } catch (error) {
      console.warn('Klaviyo popup fix: Error getting forms', error);
      return [];
    }
  }

  function isFormVisible(form) {
    try {
      if (!form || !form.isConnected || !document.body || !document.body.contains(form)) {
        return false;
      }
      const style = window.getComputedStyle(form);
      const rect = form.getBoundingClientRect();
      return (
        form.offsetParent !== null &&
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        parseFloat(style.opacity || '1') > 0 &&
        style.pointerEvents !== 'none' &&
        rect.width > 0 &&
        rect.height > 0 &&
        rect.top < window.innerHeight &&
        rect.bottom > 0 &&
        rect.left < window.innerWidth &&
        rect.right > 0
      );
    } catch (error) {
      return false;
    }
  }

  function hasVisiblePopup() {
    return getAllKlaviyoForms().some(isFormVisible);
  }

  function restorePopup() {
    try {
      const formId = getFormId();
      if (formId && window._klOnsite && !hasVisiblePopup()) {
        if (!Array.isArray(window._klOnsite)) {
          window._klOnsite = [];
        }
        window._klOnsite.push(['openForm', formId]);
      }
    } catch (error) {
      console.warn('Klaviyo popup fix: Error restoring popup', error);
    }
  }

  function stopEvent(e) {
    if (!e) return false;
    try {
      e.stopImmediatePropagation();
      e.stopPropagation();
      e.preventDefault();
    } catch (error) {
      // Event may already be handled
    }
    return false;
  }

  function isFormElement(element) {
    const tagName = element.tagName;
    return (
      tagName === 'INPUT' ||
      tagName === 'SELECT' ||
      tagName === 'TEXTAREA' ||
      tagName === 'LABEL' ||
      tagName === 'BUTTON' ||
      element.closest(SELECTORS.formElements) !== null
    );
  }

  function isElementInsideForm(element, forms) {
    if (!element || !forms || forms.length === 0) return false;

    try {
      // Explicitly exclude close buttons - they should always be allowed to work
      if (element.closest(SELECTORS.closeButton)) {
        return false;
      }

      if (forms.some(form => form && form.contains && form.contains(element))) {
        return true;
      }

      let ancestor = element;
      let depth = 0;
      const MAX_DEPTH = 15;
      while (ancestor && depth < MAX_DEPTH) {
        if (forms.some(form => form && form.contains && form.contains(ancestor))) {
          if (isFormElement(element) && !element.closest(SELECTORS.closeButton)) {
            return true;
          }
        }
        ancestor = ancestor.parentElement;
        depth++;
      }

      const tagName = element.tagName;
      // Check for form elements including buttons (but not close buttons)
      if (
        (tagName === 'INPUT' || tagName === 'SELECT' || tagName === 'TEXTAREA' || tagName === 'LABEL' || tagName === 'BUTTON') &&
        !element.closest(SELECTORS.closeButton)
      ) {
        return forms.some(form => {
          if (!form || form.offsetParent === null) return false;
          try {
            const rect = element.getBoundingClientRect();
            const formRect = form.getBoundingClientRect();
            const overlapX = Math.min(rect.right, formRect.right) - Math.max(rect.left, formRect.left);
            const overlapY = Math.min(rect.bottom, formRect.bottom) - Math.max(rect.top, formRect.top);
            return overlapX > 0 && overlapY > 0;
          } catch (error) {
            return false;
          }
        });
      }
    } catch (error) {
      console.warn('Klaviyo popup fix: Error checking element inside form', error);
    }

    return false;
  }

  function isAcceptDeclineButton(button) {
    if (!button) return false;
    return (
      COOKIE_BANNER_IDS.includes(button.id) ||
      COOKIE_BANNER_CLASSES.some(className => button.classList.contains(className))
    );
  }

  function handleCookieBannerAcceptDecline(button) {
    try {
      if (!button || !button.id) return;
      const buttonId = button.id;
      if (!COOKIE_BANNER_IDS.includes(buttonId)) return;

      const cookieValue = buttonId.includes('accept') ? 'true' : 'false';
      document.cookie = `shopify-pc__banner=${cookieValue}; path=/; max-age=31536000; SameSite=Lax`;

      const banner = document.getElementById('shopify-pc__banner');
      if (banner) {
        banner.style.display = 'none';
      }
    } catch (error) {
      console.warn('Klaviyo popup fix: Error handling cookie banner', error);
    }
  }

  function setupRestoreAttempts() {
    try {
      // Clear any existing interval
      if (restoreIntervalId !== null) {
        clearInterval(restoreIntervalId);
        restoreIntervalId = null;
      }

      cookieBannerClickTime = Date.now();
      if (!window.__klaviyoFormId) {
        window.__klaviyoFormId = getFormId();
      }

      let restoreAttempts = 0;
      restoreIntervalId = setInterval(() => {
        restoreAttempts++;
        if (!hasVisiblePopup() && cookieBannerClickTime > 0 && window.__klaviyoFormId) {
          restorePopup();
        }
        if (restoreAttempts >= RESTORE_CONFIG.maxAttempts) {
          clearInterval(restoreIntervalId);
          restoreIntervalId = null;
          cookieBannerClickTime = 0;
        }
      }, RESTORE_CONFIG.interval);

      setTimeout(() => {
        if (!hasVisiblePopup() && cookieBannerClickTime > 0) {
          restorePopup();
        }
      }, 0);
    } catch (error) {
      console.warn('Klaviyo popup fix: Error setting up restore attempts', error);
    }
  }

  function handleCookieBannerClick(e, cookieBanner) {
    try {
      if (!e || !e.target) return;
      const button = e.target.closest('button') || e.target;

      if (isAcceptDeclineButton(button)) {
        stopEvent(e);
        handleCookieBannerAcceptDecline(button);
        setupRestoreAttempts();
        return false;
      } else {
        e.stopImmediatePropagation();
        e.stopPropagation();
        return;
      }
    } catch (error) {
      console.warn('Klaviyo popup fix: Error handling cookie banner click', error);
    }
  }

  function handleOutsideInteraction(e) {
    try {
      if (!e || !e.target) return;
      if (!hasVisiblePopup()) {
        return;
      }

      // Cache forms to avoid multiple DOM queries
      const forms = getAllKlaviyoForms();

      // Always allow close button clicks
      if (e.target.closest(SELECTORS.closeButton)) {
        return;
      }

      // Always allow form submissions and submit buttons
      const target = e.target;
      const isSubmitButton = target.tagName === 'BUTTON' && 
        (target.type === 'submit' || !target.type || target.type === 'button');
      const isInsideForm = forms.some(form => form && form.contains && form.contains(target));
      
      if (isSubmitButton && isInsideForm) {
        return; // Allow submit buttons inside forms
      }

      // Allow form submit events
      if (e.type === 'submit' && target.tagName === 'FORM') {
        if (forms.some(form => form && form.contains && form.contains(target))) {
          return; // Allow form submissions
        }
      }

      const cookieBanner = e.target.closest(SELECTORS.cookieBanner);
      if (cookieBanner) {
        return handleCookieBannerClick(e, cookieBanner);
      }

      // Only block clicks on backdrop/overlay elements, not on other page elements
      const clickedBackdrop = e.target.closest(SELECTORS.backdrop);
      if (clickedBackdrop) {
        if (!forms.some(form => form && form.contains && form.contains(clickedBackdrop))) {
          return stopEvent(e);
        }
      }

      // Allow clicks on form elements
      if (isElementInsideForm(e.target, forms)) {
        return;
      }

      // Allow all other clicks (footer, navigation, etc.) to proceed normally
    } catch (error) {
      console.warn('Klaviyo popup fix: Error handling outside interaction', error);
    }
  }

  function disableBackdrop(backdrop, forms) {
    try {
      if (!backdrop || !backdrop.parentNode) return;
      if (forms.some(form => form && form.contains && form.contains(backdrop))) {
        return;
      }

      // Simply disable pointer events instead of cloning (safer and more performant)
      backdrop.style.pointerEvents = 'none';
      backdrop.style.cursor = 'default';
      
      // Remove any existing click handlers and add our own
      backdrop.onclick = null;
      backdrop.addEventListener('click', function(e) {
        // Don't block clicks on the close button
        if (e.target && e.target.closest(SELECTORS.closeButton)) {
          return;
        }
        // Don't block clicks on submit buttons or form elements
        const forms = getAllKlaviyoForms();
        if (e.target && forms.some(form => form && form.contains && form.contains(e.target))) {
          const isSubmitButton = e.target.tagName === 'BUTTON' && 
            (e.target.type === 'submit' || !e.target.type || e.target.type === 'button');
          if (isSubmitButton || isElementInsideForm(e.target, forms)) {
            return; // Allow submit buttons and form elements
          }
        }
        stopEvent(e);
      }, true);
    } catch (error) {
      console.warn('Klaviyo popup fix: Error disabling backdrop', error);
    }
  }

  function enableCloseButton() {
    try {
      const closeButtons = document.querySelectorAll(SELECTORS.closeButton);
      closeButtons.forEach(button => {
        if (button) {
          // Force enable pointer events on the close button
          button.style.pointerEvents = 'auto';
          button.style.cursor = 'pointer';
          
          // Also enable pointer events on SVG and path elements inside
          const svg = button.querySelector('svg');
          if (svg) {
            svg.style.pointerEvents = 'auto';
            svg.style.cursor = 'pointer';
          }
          const paths = button.querySelectorAll('path, circle');
          paths.forEach(path => {
            path.style.pointerEvents = 'auto';
            path.style.cursor = 'pointer';
          });
          
          // Add a direct click handler as fallback if Klaviyo's handler isn't working
          // Only add if not already added (check for data attribute)
          if (!button.dataset.klaviyoFixHandlerAdded) {
            button.dataset.klaviyoFixHandlerAdded = 'true';
            button.addEventListener('click', function(e) {
              try {
                // Find the form this button belongs to
                const form = button.closest(SELECTORS.form);
                if (form) {
                  // Try to trigger Klaviyo's close method
                  if (window._klOnsite && Array.isArray(window._klOnsite)) {
                    window._klOnsite.push(['closeForm']);
                  }
                  // Also try hiding the form directly as fallback
                  setTimeout(() => {
                    if (form && isFormVisible(form)) {
                      form.style.display = 'none';
                      form.style.visibility = 'hidden';
                      form.style.opacity = '0';
                    }
                  }, 100);
                }
              } catch (error) {
                console.warn('Klaviyo popup fix: Error in close button handler', error);
              }
            }, true);
          }
        }
      });
    } catch (error) {
      console.warn('Klaviyo popup fix: Error enabling close button', error);
    }
  }

  function disableBackdrops() {
    try {
      if (!hasVisiblePopup()) return;

      const backdrops = document.querySelectorAll(SELECTORS.backdrop);
      const forms = getAllKlaviyoForms();
      backdrops.forEach(backdrop => disableBackdrop(backdrop, forms));
      
      // Always ensure close button is clickable
      enableCloseButton();
    } catch (error) {
      console.warn('Klaviyo popup fix: Error disabling backdrops', error);
    }
  }

  function attemptRestorePopup() {
    try {
      if (cookieBannerClickTime > 0 && Date.now() - cookieBannerClickTime < RESTORE_CONFIG.cookieBannerWindow && !isRestoring) {
        isRestoring = true;
        restorePopup();

        setTimeout(() => {
          if (!hasVisiblePopup() && window.__klaviyoFormId) {
            restorePopup();
          }
          setTimeout(() => {
            if (!hasVisiblePopup() && window.__klaviyoFormId) {
              restorePopup();
            }
            isRestoring = false;
            cookieBannerClickTime = 0;
          }, RESTORE_CONFIG.timeout);
        }, RESTORE_CONFIG.interval);
      }
    } catch (error) {
      console.warn('Klaviyo popup fix: Error attempting restore', error);
      isRestoring = false;
    }
  }

  // Cleanup function
  function cleanup() {
    try {
      if (restoreIntervalId !== null) {
        clearInterval(restoreIntervalId);
        restoreIntervalId = null;
      }
      if (mutationObserver) {
        mutationObserver.disconnect();
        mutationObserver = null;
      }
    } catch (error) {
      console.warn('Klaviyo popup fix: Error during cleanup', error);
    }
  }

  // Initialize Klaviyo form ID tracking
  if (window._klOnsite && Array.isArray(window._klOnsite)) {
    try {
      const originalPush = window._klOnsite.push;
      window._klOnsite.push = function () {
        if (arguments.length >= 2 && arguments[0] === 'openForm') {
          window.__klaviyoFormId = arguments[1];
        }
        return originalPush.apply(this, arguments);
      };
    } catch (error) {
      console.warn('Klaviyo popup fix: Error intercepting _klOnsite.push', error);
    }
  }

  // Setup event listeners
  const eventTypes = ['click', 'mousedown', 'touchstart'];
  eventTypes.forEach(eventType => {
    try {
      document.addEventListener(eventType, handleOutsideInteraction, true);
      window.addEventListener(eventType, handleOutsideInteraction, true);
    } catch (error) {
      console.warn('Klaviyo popup fix: Error adding event listener', error);
    }
  });

  // Explicitly allow form submit events
  try {
    document.addEventListener('submit', function(e) {
      try {
        if (!e || !e.target) return;
        const forms = getAllKlaviyoForms();
        if (forms.some(form => form && form.contains && form.contains(e.target))) {
          // Allow form submission - don't prevent it
          return;
        }
      } catch (error) {
        console.warn('Klaviyo popup fix: Error handling submit event', error);
      }
    }, true);
  } catch (error) {
    console.warn('Klaviyo popup fix: Error adding submit event listener', error);
  }

  // Setup MutationObserver
  try {
    mutationObserver = new MutationObserver(() => {
      try {
        if (hasVisiblePopup()) {
          disableBackdrops();
        } else {
          attemptRestorePopup();
        }
      } catch (error) {
        console.warn('Klaviyo popup fix: Error in mutation observer callback', error);
      }
    });

    if (document.body) {
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden']
      });
    }
  } catch (error) {
    console.warn('Klaviyo popup fix: Error setting up mutation observer', error);
  }

  // Initial backdrop disable attempt
  setTimeout(() => {
    try {
      disableBackdrops();
    } catch (error) {
      console.warn('Klaviyo popup fix: Error in initial backdrop disable', error);
    }
  }, 2000);

  // Periodically ensure close button stays enabled (Klaviyo may reset styles)
  setInterval(() => {
    try {
      if (hasVisiblePopup()) {
        enableCloseButton();
      }
    } catch (error) {
      console.warn('Klaviyo popup fix: Error in periodic close button check', error);
    }
  }, 500);

  // Cleanup on page unload
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', cleanup);
  }
})();
